// src/pages/Campaigns/SurveyGenie/SurveyAnalytics.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Tabs,
    Tab,
    CircularProgress,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    BarChart as BarChartIcon,
    PieChart as PieChartIcon,
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    CheckCircle as CompleteIcon,
    Schedule as PendingIcon,
    Timer as TimerIcon,
    Devices as DevicesIcon,
    Language as LanguageIcon,
    Psychology as PsychologyIcon,
    SentimentSatisfiedAlt as PositiveIcon,
    SentimentDissatisfied as NegativeIcon,
    SentimentNeutral as NeutralIcon,
    OpenInNew as OpenInNewIcon,
    QuestionAnswer as QuestionIcon,
    Star as StarIcon,
    List as ListIcon
} from '@mui/icons-material';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
    AreaChart,
    Area
} from 'recharts';
import { format } from 'date-fns';
import axiosInstance from '../../../utils/axiosInstance';

// ==================== CHART COLORS ====================
const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042', '#0088FE'];
const SENTIMENT_COLORS = {
    POSITIVE: '#4caf50',
    NEGATIVE: '#f44336',
    NEUTRAL: '#9e9e9e',
    MIXED: '#ff9800'
};

// ==================== SENTIMENT SUMMARY CARD ====================
const SentimentSummaryCard = ({ surveyId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { chatbotId } = useParams();

    useEffect(() => {
        const fetchSentimentSummary = async () => {
            try {
                const [statusRes, sentimentRes] = await Promise.all([
                    axiosInstance.get('/api/surveys/analysis/status', { params: { survey_id: surveyId } }),
                    axiosInstance.get('/api/surveys/analysis/sentiment', { params: { survey_id: surveyId } })
                ]);
                
                setData({
                    status: statusRes.data.success ? statusRes.data.data : null,
                    sentiment: sentimentRes.data.success ? sentimentRes.data.data : null
                });
            } catch (error) {
                console.error('Error fetching sentiment summary:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSentimentSummary();
    }, [surveyId]);

    if (loading) {
        return (
            <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress size={30} />
                </CardContent>
            </Card>
        );
    }

    if (!data?.status?.has_text_questions) {
        return null;
    }

    const pieData = data.sentiment?.sentiment_breakdown ? [
        { name: 'Positive', value: data.sentiment.sentiment_breakdown.POSITIVE || 0, color: SENTIMENT_COLORS.POSITIVE },
        { name: 'Negative', value: data.sentiment.sentiment_breakdown.NEGATIVE || 0, color: SENTIMENT_COLORS.NEGATIVE },
        { name: 'Neutral', value: data.sentiment.sentiment_breakdown.NEUTRAL || 0, color: SENTIMENT_COLORS.NEUTRAL },
        { name: 'Mixed', value: data.sentiment.sentiment_breakdown.MIXED || 0, color: SENTIMENT_COLORS.MIXED }
    ].filter(d => d.value > 0) : [];

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PsychologyIcon color="primary" />
                    <Typography variant="h6">Sentiment Analysis</Typography>
                </Box>
                
                {/* Progress */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            Analysis Progress
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {data.status.analysis_percentage || 0}%
                        </Typography>
                    </Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={data.status.analysis_percentage || 0}
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {data.status.analyzed_answers || 0} of {data.status.total_text_answers || 0} responses
                    </Typography>
                </Box>

                {/* Mini pie chart */}
                {pieData.length > 0 && (
                    <Box sx={{ height: 150, mb: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={35}
                                    outerRadius={55}
                                    paddingAngle={2}
                                    dataKey="value"
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <RechartsTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                )}

                {/* Quick stats */}
                <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: SENTIMENT_COLORS.POSITIVE }}>
                            {data.sentiment?.sentiment_breakdown?.POSITIVE || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Positive</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: SENTIMENT_COLORS.NEGATIVE }}>
                            {data.sentiment?.sentiment_breakdown?.NEGATIVE || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Negative</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: SENTIMENT_COLORS.NEUTRAL }}>
                            {data.sentiment?.sentiment_breakdown?.NEUTRAL || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Neutral</Typography>
                    </Box>
                </Box>

                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PsychologyIcon />}
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => navigate(`/chatbot/${chatbotId}/surveys/text-analysis/${surveyId}`)}
                >
                    View Full Analysis
                </Button>
            </CardContent>
        </Card>
    );
};

// ==================== TEXT ANALYSIS TAB CONTENT ====================
const TextAnalysisTabContent = ({ surveyId }) => {
    const [loading, setLoading] = useState(true);
    const [sentimentData, setSentimentData] = useState(null);
    const [keywordData, setKeywordData] = useState(null);
    const [trendData, setTrendData] = useState(null);
    const [quotesData, setQuotesData] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const navigate = useNavigate();
    const { chatbotId } = useParams();
    const theme = useTheme();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [sentimentRes, keywordRes, trendRes, quotesRes] = await Promise.all([
                axiosInstance.get('/api/surveys/analysis/sentiment', { params: { survey_id: surveyId } }),
                axiosInstance.get('/api/surveys/analysis/keywords', { params: { survey_id: surveyId } }),
                axiosInstance.get('/api/surveys/analysis/sentiment-trend', { params: { survey_id: surveyId, days: 30 } }),
                axiosInstance.get('/api/surveys/analysis/quotes', { params: { survey_id: surveyId, max_quotes: 3 } })
            ]);

            if (sentimentRes.data.success) setSentimentData(sentimentRes.data.data);
            if (keywordRes.data.success) setKeywordData(keywordRes.data.data);
            if (trendRes.data.success) setTrendData(trendRes.data.data);
            if (quotesRes.data.success) setQuotesData(quotesRes.data.data);
        } catch (error) {
            console.error('Error fetching text analysis data:', error);
        } finally {
            setLoading(false);
        }
    }, [surveyId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const triggerAnalysis = async () => {
        setAnalyzing(true);
        try {
            await axiosInstance.post('/api/surveys/analysis/trigger', {
                survey_id: parseInt(surveyId),
                force_reanalyze: true
            });
            await fetchData();
        } catch (error) {
            console.error('Error triggering analysis:', error);
        } finally {
            setAnalyzing(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const pieChartData = sentimentData?.sentiment_breakdown ? 
        Object.entries(sentimentData.sentiment_breakdown)
            .map(([name, value]) => ({
                name: name.charAt(0) + name.slice(1).toLowerCase(),
                value,
                color: SENTIMENT_COLORS[name]
            }))
            .filter(d => d.value > 0) : [];

    return (
        <Box>
            {/* Header Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Text & Sentiment Analysis</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={analyzing ? <CircularProgress size={20} /> : <RefreshIcon />}
                        onClick={triggerAnalysis}
                        disabled={analyzing}
                    >
                        {analyzing ? 'Analyzing...' : 'Re-analyze'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PsychologyIcon />}
                        onClick={() => navigate(`/chatbot/${chatbotId}/surveys/text-analysis/${surveyId}`)}
                    >
                        Full Analysis Dashboard
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Sentiment Overview Cards */}
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        {[
                            { key: 'POSITIVE', icon: <PositiveIcon />, label: 'Positive' },
                            { key: 'NEGATIVE', icon: <NegativeIcon />, label: 'Negative' },
                            { key: 'NEUTRAL', icon: <NeutralIcon />, label: 'Neutral' },
                            { key: 'MIXED', icon: <PsychologyIcon />, label: 'Mixed' }
                        ].map(({ key, icon, label }) => (
                            <Grid item xs={6} md={3} key={key}>
                                <Card>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                            {React.cloneElement(icon, { sx: { color: SENTIMENT_COLORS[key], fontSize: 32 } })}
                                        </Box>
                                        <Typography variant="h4" fontWeight={600}>
                                            {sentimentData?.sentiment_breakdown?.[key] || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {label}
                                        </Typography>
                                        {sentimentData?.total_analyzed > 0 && (
                                            <Typography variant="caption" color="text.secondary">
                                                {((sentimentData.sentiment_breakdown?.[key] || 0) / sentimentData.total_analyzed * 100).toFixed(1)}%
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                {/* Sentiment Distribution */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Sentiment Distribution
                        </Typography>
                        {pieChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie
                                        data={pieChartData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={2}
                                        dataKey="value"
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {pieChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                                <Typography color="text.secondary">No sentiment data available</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Sentiment Trend */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Sentiment Trend (30 Days)
                        </Typography>
                        {trendData?.trend?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <AreaChart data={trendData.trend}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="date" 
                                        tickFormatter={(val) => format(new Date(val), 'MMM d')}
                                    />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Area type="monotone" dataKey="positive" stackId="1" stroke={SENTIMENT_COLORS.POSITIVE} fill={SENTIMENT_COLORS.POSITIVE} fillOpacity={0.6} />
                                    <Area type="monotone" dataKey="neutral" stackId="1" stroke={SENTIMENT_COLORS.NEUTRAL} fill={SENTIMENT_COLORS.NEUTRAL} fillOpacity={0.6} />
                                    <Area type="monotone" dataKey="mixed" stackId="1" stroke={SENTIMENT_COLORS.MIXED} fill={SENTIMENT_COLORS.MIXED} fillOpacity={0.6} />
                                    <Area type="monotone" dataKey="negative" stackId="1" stroke={SENTIMENT_COLORS.NEGATIVE} fill={SENTIMENT_COLORS.NEGATIVE} fillOpacity={0.6} />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                                <Typography color="text.secondary">No trend data available</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Top Keywords */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Top Keywords
                        </Typography>
                        {keywordData?.keywords?.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {keywordData.keywords.slice(0, 20).map(({ word, count }, index) => (
                                    <Chip
                                        key={word}
                                        label={`${word} (${count})`}
                                        size="small"
                                        sx={{
                                            backgroundColor: index < 5 ? theme.palette.primary.light : undefined,
                                            color: index < 5 ? theme.palette.primary.contrastText : undefined
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Typography color="text.secondary">No keywords extracted</Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Representative Quotes */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Representative Quotes
                        </Typography>
                        {quotesData?.POSITIVE?.length > 0 || quotesData?.NEGATIVE?.length > 0 ? (
                            <Box>
                                {quotesData.POSITIVE?.slice(0, 2).map((quote, idx) => (
                                    <Box 
                                        key={idx} 
                                        sx={{ 
                                            p: 1.5, 
                                            mb: 1, 
                                            borderLeft: `3px solid ${SENTIMENT_COLORS.POSITIVE}`,
                                            bgcolor: 'grey.50',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                            "{quote.text?.substring(0, 150)}..."
                                        </Typography>
                                        <Chip label="Positive" size="small" color="success" sx={{ mt: 1 }} />
                                    </Box>
                                ))}
                                {quotesData.NEGATIVE?.slice(0, 2).map((quote, idx) => (
                                    <Box 
                                        key={idx} 
                                        sx={{ 
                                            p: 1.5, 
                                            mb: 1, 
                                            borderLeft: `3px solid ${SENTIMENT_COLORS.NEGATIVE}`,
                                            bgcolor: 'grey.50',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                            "{quote.text?.substring(0, 150)}..."
                                        </Typography>
                                        <Chip label="Negative" size="small" color="error" sx={{ mt: 1 }} />
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography color="text.secondary">No quotes available</Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

// ==================== MAIN COMPONENT ====================
const SurveyAnalytics = () => {
    const { chatbotId, surveyId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();

    // State
    const [loading, setLoading] = useState(true);
    const [survey, setSurvey] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [dateRange, setDateRange] = useState('30');
    const [selectedQuestion, setSelectedQuestion] = useState('all');

    // Fetch survey and analytics
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [surveyRes, analyticsRes] = await Promise.all([
                axiosInstance.get(`/api/surveys/${surveyId}`),
                axiosInstance.get('/api/surveys/analytics', {
                    params: { survey_id: surveyId, days: dateRange }
                })
            ]);

            if (surveyRes.data.success) setSurvey(surveyRes.data.data);
            if (analyticsRes.data.success) setAnalytics(analyticsRes.data.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [surveyId, dateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Tab configuration
    const tabs = [
        { label: 'Overview', icon: <BarChartIcon /> },
        { label: 'Questions', icon: <QuestionIcon /> },
        { label: 'Response Trend', icon: <TrendingUpIcon /> },
        { label: 'Text Analysis', icon: <PsychologyIcon /> },
        { label: 'Demographics', icon: <PeopleIcon /> }
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Calculate stats
    const totalResponses = analytics?.total_responses || 0;
    const completedResponses = analytics?.completed_responses || 0;
    const completionRate = analytics?.completion_rate || 0;
    const avgDuration = analytics?.average_duration_seconds 
        ? `${Math.floor(analytics.average_duration_seconds / 60)}m ${analytics.average_duration_seconds % 60}s`
        : 'N/A';

    return (
        <Box sx={{ p: 3, mt: 10 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/surveys`)}>
                        <BackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>
                            Survey Analytics
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {survey?.title || 'Loading...'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Date Range</InputLabel>
                        <Select
                            value={dateRange}
                            label="Date Range"
                            onChange={(e) => setDateRange(e.target.value)}
                        >
                            <MenuItem value="7">Last 7 days</MenuItem>
                            <MenuItem value="30">Last 30 days</MenuItem>
                            <MenuItem value="90">Last 90 days</MenuItem>
                            <MenuItem value="365">Last year</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchData}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button variant="outlined" startIcon={<DownloadIcon />}>
                        Export
                    </Button>
                </Box>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(e, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {tabs.map((tab, index) => (
                        <Tab 
                            key={index}
                            icon={tab.icon} 
                            label={tab.label}
                            iconPosition="start"
                        />
                    ))}
                </Tabs>
            </Paper>

            {/* TAB 0: OVERVIEW */}
            {activeTab === 0 && (
                <Grid container spacing={3}>
                    {/* Key Metrics */}
                    <Grid item xs={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <PeopleIcon color="primary" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Total Responses
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight={600}>
                                    {totalResponses}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <CompleteIcon color="success" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Completion Rate
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight={600}>
                                    {completionRate}%
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <TimerIcon color="info" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Avg. Duration
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight={600}>
                                    {avgDuration}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <QuestionIcon color="warning" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Questions
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight={600}>
                                    {survey?.questions?.length || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Response Status Distribution */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Response Status
                            </Typography>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={[
                                            { name: 'Completed', value: analytics?.completed_responses || 0 },
                                            { name: 'In Progress', value: analytics?.in_progress_responses || 0 },
                                            { name: 'Abandoned', value: analytics?.abandoned_responses || 0 }
                                        ].filter(d => d.value > 0)}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    >
                                        <Cell fill="#4caf50" />
                                        <Cell fill="#ff9800" />
                                        <Cell fill="#f44336" />
                                    </Pie>
                                    <RechartsTooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Sentiment Analysis Summary */}
                    <Grid item xs={12} md={6}>
                        <SentimentSummaryCard surveyId={surveyId} />
                    </Grid>

                    {/* Device Distribution */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Device Distribution
                            </Typography>
                            {analytics?.device_breakdown?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={analytics.device_breakdown}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="device" />
                                        <YAxis />
                                        <RechartsTooltip />
                                        <Bar dataKey="count" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                                    <Typography color="text.secondary">No device data available</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Response Trend Mini */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Response Trend
                            </Typography>
                            {analytics?.daily_responses?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={analytics.daily_responses}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="date" 
                                            tickFormatter={(val) => format(new Date(val), 'MMM d')}
                                        />
                                        <YAxis />
                                        <RechartsTooltip />
                                        <Line 
                                            type="monotone" 
                                            dataKey="count" 
                                            stroke={theme.palette.primary.main}
                                            strokeWidth={2}
                                            dot={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                                    <Typography color="text.secondary">No trend data available</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* TAB 1: QUESTIONS */}
            {activeTab === 1 && (
                <Box>
                    <Box sx={{ mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 300 }}>
                            <InputLabel>Select Question</InputLabel>
                            <Select
                                value={selectedQuestion}
                                label="Select Question"
                                onChange={(e) => setSelectedQuestion(e.target.value)}
                            >
                                <MenuItem value="all">All Questions</MenuItem>
                                {survey?.questions?.map((q, idx) => (
                                    <MenuItem key={q.id} value={q.id}>
                                        Q{idx + 1}: {q.question_text?.substring(0, 50)}...
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Grid container spacing={3}>
                        {(selectedQuestion === 'all' ? survey?.questions : survey?.questions?.filter(q => q.id === selectedQuestion))?.map((question, idx) => {
                            const questionAnalytics = analytics?.question_analytics?.find(qa => qa.question_id === question.id);
                            
                            return (
                                <Grid item xs={12} md={6} key={question.id}>
                                    <Paper sx={{ p: 3, height: '100%' }}>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                            Q{idx + 1}: {question.question_text}
                                        </Typography>
                                        <Chip 
                                            label={question.question_type?.replace('_', ' ')} 
                                            size="small" 
                                            sx={{ mb: 2 }}
                                        />
                                        
                                        {/* CASE 1: Choice Questions (Charts) */}
                                        {questionAnalytics?.options?.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={200}>
                                                <BarChart data={questionAnalytics.options} layout="vertical">
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis type="number" />
                                                    <YAxis dataKey="option" type="category" width={100} tick={{fontSize: 11}} />
                                                    <RechartsTooltip />
                                                    <Bar dataKey="count" fill={CHART_COLORS[idx % CHART_COLORS.length]} radius={[0, 4, 4, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : 
                                        
                                        /* CASE 2: Rating/Scale Questions (Stats) */
                                        ['RATING_SCALE', 'NET_PROMOTER_SCORE', 'NUMBER', 'SLIDER'].includes(question.question_type) ? (
                                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                                <Typography variant="h3" color="primary.main" fontWeight="bold">
                                                    {questionAnalytics?.average_value !== null ? questionAnalytics.average_value : '-'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Average Rating
                                                </Typography>
                                                
                                                {/* Star Visualization for Rating */}
                                                {question.question_type === 'RATING_SCALE' && questionAnalytics?.average_value && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                            <StarIcon 
                                                                key={star} 
                                                                sx={{ 
                                                                    color: star <= Math.round(questionAnalytics.average_value) ? '#ffc107' : '#e0e0e0',
                                                                    fontSize: 24
                                                                }} 
                                                            />
                                                        ))}
                                                    </Box>
                                                )}

                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
                                                    <Box>
                                                        <Typography variant="caption" display="block">Min</Typography>
                                                        <Typography fontWeight="bold">{questionAnalytics?.min_value ?? '-'}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" display="block">Max</Typography>
                                                        <Typography fontWeight="bold">{questionAnalytics?.max_value ?? '-'}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" display="block">Responses</Typography>
                                                        <Typography fontWeight="bold">{questionAnalytics?.total_answers || 0}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ) : 
                                        
                                        /* CASE 3: Email/Phone/Text (Summary) */
                                        ['EMAIL', 'PHONE', 'TEXT_SHORT', 'TEXT_LONG', 'DATE'].includes(question.question_type) ? (
                                            <Box sx={{ py: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <ListIcon color="action" />
                                                        <Typography variant="body2">Total Responses</Typography>
                                                    </Box>
                                                    <Typography variant="h6">{questionAnalytics?.total_answers || 0}</Typography>
                                                </Box>
                                                
                                                {['TEXT_SHORT', 'TEXT_LONG'].includes(question.question_type) && (
                                                    <Button 
                                                        variant="outlined" 
                                                        fullWidth 
                                                        size="small"
                                                        onClick={() => setActiveTab(3)} // Jump to Text Analysis tab
                                                    >
                                                        View Sentiment Analysis
                                                    </Button>
                                                )}
                                                
                                                {['EMAIL', 'PHONE'].includes(question.question_type) && (
                                                    <Typography variant="caption" color="text.secondary" align="center" display="block">
                                                        Go to "Responses" page to view contact details.
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            /* Default Fallback */
                                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                                <Typography color="text.secondary">
                                                    No response data available
                                                </Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}

            {/* TAB 2: RESPONSE TREND */}
            {activeTab === 2 && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Response Trend Over Time
                    </Typography>
                    {analytics?.daily_responses?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={400}>
                            <AreaChart data={analytics.daily_responses}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="date" 
                                    tickFormatter={(val) => format(new Date(val), 'MMM d')}
                                />
                                <YAxis />
                                <RechartsTooltip 
                                    labelFormatter={(val) => format(new Date(val), 'PPP')}
                                />
                                <Legend />
                                <Area 
                                    type="monotone" 
                                    dataKey="count" 
                                    name="Responses"
                                    stroke={theme.palette.primary.main}
                                    fill={theme.palette.primary.light}
                                    fillOpacity={0.6}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                            <Typography color="text.secondary">No trend data available</Typography>
                        </Box>
                    )}
                </Paper>
            )}

            {/* TAB 3: TEXT ANALYSIS */}
            {activeTab === 3 && (
                <TextAnalysisTabContent surveyId={surveyId} />
            )}

            {/* TAB 4: DEMOGRAPHICS */}
            {activeTab === 4 && (
                <Grid container spacing={3}>
                    {/* Country Distribution */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Country Distribution
                            </Typography>
                            {analytics?.country_breakdown?.length > 0 ? (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Country</TableCell>
                                                <TableCell align="right">Responses</TableCell>
                                                <TableCell align="right">%</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {analytics.country_breakdown.slice(0, 10).map((row) => (
                                                <TableRow key={row.country}>
                                                    <TableCell>{row.country || 'Unknown'}</TableCell>
                                                    <TableCell align="right">{row.count}</TableCell>
                                                    <TableCell align="right">
                                                        {((row.count / totalResponses) * 100).toFixed(1)}%
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                                    No country data available
                                </Typography>
                            )}
                        </Paper>
                    </Grid>

                    {/* Device Distribution */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                <DevicesIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Device Distribution
                            </Typography>
                            {analytics?.device_breakdown?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={analytics.device_breakdown}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            dataKey="count"
                                            nameKey="device"
                                            label={({ device, percent }) => `${device} ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {analytics.device_breakdown.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                                    No device data available
                                </Typography>
                            )}
                        </Paper>
                    </Grid>

                    {/* Browser Distribution */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Browser Distribution
                            </Typography>
                            {analytics?.browser_breakdown?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={analytics.browser_breakdown}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="browser" />
                                        <YAxis />
                                        <RechartsTooltip />
                                        <Bar dataKey="count" fill={theme.palette.secondary.main} radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                                    No browser data available
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default SurveyAnalytics;